import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Building,
  Calendar,
  ChevronDown,
  Filter,
  Smile,
  Search,
  X,
} from 'lucide-react';
import AccordionPanel from '../../../components/AccordionPanel';
import {
  GEO_DATE_RANGE_OPTIONS,
  GEO_SENTIMENT_TYPE_OPTIONS,
} from '../utils/geoConstants';
import {
  GEO_COMPETITORS_DEFAULT,
  GEO_FILTER_DEFAULTS,
} from '../utils/geoFilters';
import { parseMultiSelect, serializeMultiSelect } from '../../../utils/filtersApi';
import { compareGeoQueryIds, formatCompanyName } from '../utils/geoPresentation';

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="custom-date-input-wrapper" onClick={onClick} ref={ref}>
    <input
      type="text"
      readOnly
      value={value}
      placeholder={placeholder || 'MM-DD-YYYY'}
      className="custom-date-input-field"
    />
  </div>
));

function getSelected(currentVal, defaultVal, catalog = null) {
  return parseMultiSelect(currentVal, defaultVal, catalog);
}

function countSelected(currentVal, defaultVal, catalog = null) {
  return currentVal === defaultVal ? 0 : getSelected(currentVal, defaultVal, catalog).length;
}

function isOptionSelected(currentVal, defaultVal, option, catalog = null) {
  return currentVal !== defaultVal && getSelected(currentVal, defaultVal, catalog).includes(option);
}

function toggleMultiSelectFilter(currentVal, option, defaultVal, setter, catalog = null) {
  if (option === defaultVal) {
    setter(defaultVal);
    return;
  }
  let selected = currentVal === defaultVal ? [] : getSelected(currentVal, defaultVal, catalog);
  if (selected.includes(option)) {
    selected = selected.filter((x) => x !== option);
  } else {
    selected.push(option);
  }
  setter(selected.length === 0 ? defaultVal : serializeMultiSelect(selected));
}

function handleSelectAllToggle(currentVal, optionsList, defaultVal, setter, catalog = null) {
  const catalogForParse = catalog || optionsList;
  const selected = currentVal === defaultVal ? [] : getSelected(currentVal, defaultVal, catalogForParse);
  if (selected.length === optionsList.length) {
    setter(defaultVal);
  } else {
    setter(serializeMultiSelect(optionsList));
  }
}

function buildDraftFromApplied(filters) {
  const {
    days,
    useCustomRange,
    dateFrom,
    dateTo,
    filterCompetitors,
    sentimentType,
  } = filters;

  return {
    days,
    useCustomRange,
    dateFrom,
    dateTo,
    tempDateRange: useCustomRange ? [dateFrom, dateTo] : [null, null],
    filterCompetitors,
    sentimentType,
  };
}

function buildDefaultDraft(defaultDays) {
  return {
    days: defaultDays,
    useCustomRange: false,
    dateFrom: null,
    dateTo: null,
    tempDateRange: [null, null],
    filterCompetitors: GEO_COMPETITORS_DEFAULT,
    sentimentType: GEO_FILTER_DEFAULTS.sentimentType,
  };
}

function AccordionHeader({ sectionKey, openFilter, setOpenFilter, setFilterSearch, icon: Icon, title, count }) {
  return (
    <div
      className="accordion-header"
      onClick={() => {
        setOpenFilter(openFilter === sectionKey ? null : sectionKey);
        setFilterSearch('');
      }}
    >
      <div className="accordion-header-left">
        <Icon size={18} className="accordion-header-icon" />
        <span className="accordion-header-title">{title}</span>
      </div>
      <div className="accordion-header-right">
        {count ? (
          <span className="accordion-selected-count">{count}</span>
        ) : null}
        <ChevronDown
          size={16}
          className={`accordion-chevron ${openFilter === sectionKey ? 'is-open' : ''}`}
        />
      </div>
    </div>
  );
}

function FilterBadges({ currentValue, defaultValue, setValue, catalog = null, formatLabel = (item) => item }) {
  if (!currentValue || currentValue === defaultValue) return null;
  const items = getSelected(currentValue, defaultValue, catalog);
  return (
    <div className="filters-badge-container">
      {items.map((item) => (
        <div key={item} className="filters-badge">
          <span>{formatLabel(item)}</span>
          <button
            type="button"
            onClick={() => {
              const values = getSelected(currentValue, defaultValue, catalog).filter((v) => v !== item);
              setValue(values.length === 0 ? defaultValue : serializeMultiSelect(values));
            }}
            className="filters-badge-cancel"
          >
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

function MultiSelectFilterSection({
  sectionKey,
  openFilter,
  setOpenFilter,
  setFilterSearch,
  filterSearch,
  title,
  icon,
  options,
  currentValue,
  setValue,
  defaultLabel,
  catalog,
  formatOptionLabel = (label) => label,
  searchPlaceholder,
}) {
  const filtered = options
    .filter((opt) => {
      const label = typeof opt === 'string' ? opt : opt.label;
      return String(label || '').toLowerCase().includes(filterSearch.toLowerCase());
    })
    .sort((a, b) => {
      const idA = typeof a === 'string' ? a : a.value;
      const idB = typeof b === 'string' ? b : b.value;
      return compareGeoQueryIds(idA, idB);
    });

  const optionValues = options.map((opt) => (typeof opt === 'string' ? opt : opt.value));

  return (
    <div className="accordion-item">
      <AccordionHeader
        sectionKey={sectionKey}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        setFilterSearch={setFilterSearch}
        icon={icon}
        title={title}
        count={countSelected(currentValue, defaultLabel, catalog)}
      />
      <AccordionPanel isOpen={openFilter === sectionKey}>
        <div className="custom-dropdown-header accordion-search-container">
          <div className="custom-dropdown-search-wrap">
            <Search size={16} className="custom-dropdown-search-icon" />
            <input
              type="text"
              className="custom-dropdown-search-input accordion-search-input"
              placeholder={searchPlaceholder}
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
            />
          </div>
        </div>
        <div
          className="custom-dropdown-item select-all"
          onClick={() => handleSelectAllToggle(currentValue, optionValues, defaultLabel, setValue, catalog)}
        >
          <input
            type="checkbox"
            className="custom-dropdown-checkbox"
            checked={currentValue !== defaultLabel && countSelected(currentValue, defaultLabel, catalog) === optionValues.length}
            readOnly
          />
          <span>Select all</span>
        </div>
        <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
          {filtered.length === 0 ? (
            <div className="custom-dropdown-item" style={{ cursor: 'default', opacity: 0.7 }}>
              <span>No matches</span>
            </div>
          ) : (
            filtered.map((opt) => {
              const value = typeof opt === 'string' ? opt : opt.value;
              const label = typeof opt === 'string' ? opt : opt.label;
              const isChecked = isOptionSelected(currentValue, defaultLabel, value, catalog);
              return (
                <div
                  key={value}
                  className="custom-dropdown-item"
                  onClick={() => toggleMultiSelectFilter(currentValue, value, defaultLabel, setValue, catalog)}
                >
                  <input
                    type="checkbox"
                    className="custom-dropdown-checkbox"
                    checked={isChecked}
                    readOnly
                  />
                  <span title={label}>{formatOptionLabel(label)}</span>
                </div>
              );
            })
          )}
        </div>
        <FilterBadges
          currentValue={currentValue}
          defaultValue={defaultLabel}
          setValue={setValue}
          catalog={catalog}
          formatLabel={formatOptionLabel}
        />
      </AccordionPanel>
    </div>
  );
}

function SingleSelectFilterSection({
  sectionKey,
  openFilter,
  setOpenFilter,
  setFilterSearch,
  title,
  icon,
  options,
  value,
  onSelect,
  defaultValue,
}) {
  const selectedCount = value !== defaultValue ? 1 : 0;

  return (
    <div className="accordion-item">
      <AccordionHeader
        sectionKey={sectionKey}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        setFilterSearch={setFilterSearch}
        icon={icon}
        title={title}
        count={selectedCount || null}
      />
      <AccordionPanel isOpen={openFilter === sectionKey}>
        <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
          {options.map((opt) => {
            const isChecked = String(value) === String(opt.value);
            return (
              <div
                key={opt.value}
                className="custom-dropdown-item"
                onClick={() => onSelect(opt.value)}
              >
                <input
                  type="checkbox"
                  className="custom-dropdown-checkbox"
                  checked={isChecked}
                  readOnly
                />
                <span>{opt.label}</span>
              </div>
            );
          })}
        </div>
      </AccordionPanel>
    </div>
  );
}

function DateRangeFilterSection({
  openFilter,
  setOpenFilter,
  setFilterSearch,
  title,
  dateSelectValue,
  handleDaysChange,
  useCustomRange,
  tempDateRange,
  setTempDateRange,
  dateFrom,
  dateTo,
  applyCustomDateRange,
  defaultDays,
}) {
  const datePickerRef = useRef(null);
  const selectedCount = useCustomRange || Number(dateSelectValue) !== defaultDays ? 1 : 0;

  return (
    <div className="accordion-item">
      <AccordionHeader
        sectionKey="dateRange"
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        setFilterSearch={setFilterSearch}
        icon={Calendar}
        title={title}
        count={selectedCount || null}
      />
      <AccordionPanel isOpen={openFilter === 'dateRange'}>
        <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
          {GEO_DATE_RANGE_OPTIONS.map((opt) => {
            const isChecked = String(dateSelectValue) === String(opt.value);
            return (
              <div
                key={opt.value}
                className="custom-dropdown-item"
                onClick={() => handleDaysChange(String(opt.value))}
              >
                <input
                  type="checkbox"
                  className="custom-dropdown-checkbox"
                  checked={isChecked}
                  readOnly
                />
                <span>{opt.label}</span>
              </div>
            );
          })}
        </div>
        {useCustomRange ? (
          <div className="filters-date-range-picker geo-filters-drawer__custom-range">
            <DatePicker
              selectsRange
              startDate={tempDateRange[0]}
              endDate={tempDateRange[1]}
              onChange={(range) => setTempDateRange(range || [null, null])}
              onCalendarOpen={() => setTempDateRange([dateFrom, dateTo])}
              maxDate={new Date()}
              minDate={new Date('1951-03-31')}
              isClearable
              placeholderText="MM/DD/YYYY"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={80}
              dropdownMode="scroll"
              popperPlacement="bottom-end"
              shouldCloseOnSelect={false}
              ref={datePickerRef}
              customInput={<CustomDateInput />}
            >
              <div className="calendar-confirm-bar">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    applyCustomDateRange();
                    datePickerRef.current?.setOpen(false);
                  }}
                  disabled={!tempDateRange?.[0] || !tempDateRange?.[1]}
                >
                  Apply Range
                </button>
              </div>
            </DatePicker>
          </div>
        ) : null}
      </AccordionPanel>
    </div>
  );
}

export default function GeoFiltersDrawer({
  isOpen,
  onClose,
  filters,
  onApply,
}) {
  const {
    mode,
    defaultDays,
    applyFilters,
    competitorCatalog,
  } = filters;

  const [draft, setDraft] = useState(() => buildDraftFromApplied(filters));
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');

  const dateSelectValue = draft.useCustomRange ? 'custom' : draft.days;
  const dateRangeTitle = mode === 'prompts' ? 'Metrics date range' : 'Date range';

  useEffect(() => {
    if (isOpen) {
      setDraft(buildDraftFromApplied(filters));
      setOpenFilter(null);
      setFilterSearch('');
    }
  }, [isOpen, filters.days, filters.useCustomRange, filters.dateFrom, filters.dateTo, filters.filterCompetitors, filters.sentimentType]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.accordion-item') && !e.target.closest('.react-datepicker')) {
        setOpenFilter(null);
        setFilterSearch('');
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  const handleDraftDaysChange = (value) => {
    if (value === 'custom') {
      setDraft((current) => ({
        ...current,
        useCustomRange: true,
        tempDateRange: [current.dateFrom, current.dateTo],
      }));
      return;
    }
    setDraft((current) => ({
      ...current,
      useCustomRange: false,
      days: Number(value),
      dateFrom: null,
      dateTo: null,
      tempDateRange: [null, null],
    }));
  };

  const applyDraftCustomDateRange = () => {
    const [start, end] = draft.tempDateRange;
    setDraft((current) => ({
      ...current,
      useCustomRange: true,
      dateFrom: start || null,
      dateTo: end || null,
    }));
  };

  const handleApply = () => {
    let nextDateFrom = draft.dateFrom;
    let nextDateTo = draft.dateTo;

    if (draft.useCustomRange) {
      const [start, end] = draft.tempDateRange;
      if (start && end) {
        nextDateFrom = start;
        nextDateTo = end;
      }
    }

    const nextFilters = {
      days: draft.days,
      useCustomRange: draft.useCustomRange,
      dateFrom: nextDateFrom,
      dateTo: nextDateTo,
      filterCompetitors: draft.filterCompetitors,
      sentimentType: draft.sentimentType,
    };

    if (onApply) {
      onApply(nextFilters);
    } else {
      applyFilters(nextFilters);
    }
    onClose();
  };

  const handleReset = () => {
    setDraft(buildDefaultDraft(defaultDays));
  };

  return (
    <>
      <div
        className={`drawer-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <aside
        className={`details-drawer filters-details-drawer geo-filters-drawer ${isOpen ? 'open' : ''}`}
        aria-label="Prompt Pulse filters"
        aria-hidden={!isOpen}
      >
        <div className="drawer-header">
          <h3 className="drawer-title filters-drawer-title-wrap">
            <Filter size={18} />
            <span>Filters</span>
          </h3>
          <button type="button" className="drawer-close filters-drawer-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="drawer-body filters-drawer-body">
          <DateRangeFilterSection
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            setFilterSearch={setFilterSearch}
            title={dateRangeTitle}
            dateSelectValue={dateSelectValue}
            handleDaysChange={handleDraftDaysChange}
            useCustomRange={draft.useCustomRange}
            tempDateRange={draft.tempDateRange}
            setTempDateRange={(range) => setDraft((current) => ({
              ...current,
              tempDateRange: range || [null, null],
            }))}
            dateFrom={draft.dateFrom}
            dateTo={draft.dateTo}
            applyCustomDateRange={applyDraftCustomDateRange}
            defaultDays={defaultDays}
          />

          {mode === 'prompts' ? (
            <SingleSelectFilterSection
              sectionKey="sentimentType"
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              setFilterSearch={setFilterSearch}
              title="Sentiment type"
              icon={Smile}
              options={GEO_SENTIMENT_TYPE_OPTIONS}
              value={draft.sentimentType}
              defaultValue={GEO_FILTER_DEFAULTS.sentimentType}
              onSelect={(value) => setDraft((current) => ({ ...current, sentimentType: value }))}
            />
          ) : null}

          {mode === 'insights' ? (
            <MultiSelectFilterSection
              sectionKey="competitors"
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              setFilterSearch={setFilterSearch}
              filterSearch={filterSearch}
              title="Competitors"
              icon={Building}
              options={competitorCatalog}
              currentValue={draft.filterCompetitors}
              setValue={(value) => setDraft((current) => ({ ...current, filterCompetitors: value }))}
              defaultLabel={GEO_COMPETITORS_DEFAULT}
              catalog={competitorCatalog}
              formatOptionLabel={(label) => formatCompanyName(label, 36)}
              searchPlaceholder="Search competitors"
            />
          ) : null}
        </div>
        <div className="drawer-footer filters-drawer-footer">
          <button
            type="button"
            className="btn btn-sm filters-drawer-btn-reset"
            onClick={handleReset}
          >
            Reset
          </button>
          <button type="button" className="btn btn-sm filters-drawer-btn-apply" onClick={handleApply}>
            Apply
          </button>
        </div>
      </aside>
    </>
  );
}
